import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"; // Add this import
import HeroSection from "../components/HeroSection";
import { get } from "../api/config";

const CompletedProjects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate(); // Add navigate hook

  useEffect(() => {
    fetchCompletedProjects();
  }, []);

  const fetchCompletedProjects = async () => {
    try {
      setLoading(true);
      const response = await get("public/projects?status_id=3");

      if (response.data && Array.isArray(response.data)) {
        setProjects(response.data);
      } else {
        setProjects([]);
      }
    } catch (err) {
      setError("Failed to fetch completed projects");
      console.error("Error fetching projects:", err);
    } finally {
      setLoading(false);
    }
  };

  // Add click handler for project cards
  const handleProjectClick = (projectId) => {
    navigate(`/projects/${projectId}`);
  };

  if (loading) {
    return (
      <div className="w-full bg-white py-12 px-4">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 inline-block relative pb-2">
              Completed Projects
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
            </h2>
          </div>
          <div className="text-center py-8">
            <div className="text-gray-600">Loading completed projects...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white py-12 px-4">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-green-600 inline-block relative pb-2">
              Completed Projects
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
            </h2>
          </div>
          <div className="text-center py-8">
            <div className="text-red-600 mb-4">{error}</div>
            <button
              onClick={fetchCompletedProjects}
              className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white py-12 px-4">
      <HeroSection />

      <div className="mx-auto max-w-6xl px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-green-600 inline-block relative pb-2">
            Completed Projects
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-600"></div>
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            Successfully delivered infrastructure projects that have made a
            positive impact in our community.
          </p>
        </div>

        {projects.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg mb-2">
              No completed projects yet
            </div>
            <p className="text-gray-400">
              All successfully delivered projects will appear here.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {projects.map((project) => (
                <ProjectCard 
                  key={project.project_id} 
                  project={project} 
                  onProjectClick={handleProjectClick} // Pass click handler
                />
              ))}
            </div>

            <div className="text-center">
              <div className="text-gray-600 mb-4">
                Showing {projects.length} completed project
                {projects.length !== 1 ? "s" : ""}
              </div>
              <button className="text-green-600 hover:text-green-700 font-semibold underline transition-colors">
                View All Completed Projects
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ProjectCard = ({ project, onProjectClick }) => { // Accept onProjectClick prop
  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCost = (cost) => {
    if (!cost) return "₹ 0";
    const amount = parseFloat(cost);
    if (amount >= 10000000) {
      return `₹ ${(amount / 10000000).toFixed(1)} Cr`;
    } else if (amount >= 100000) {
      return `₹ ${(amount / 100000).toFixed(1)} L`;
    }
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getProjectImage = (project) => {
    const projectName = project.project_name?.toLowerCase() || "";

    if (projectName.includes("drain")) {
      return "https://images.unsplash.com/photo-1581094794329-c8112c4e5190?w=400&h=300&fit=crop";
    } else if (projectName.includes("road")) {
      return "https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=400&h=300&fit=crop";
    } else {
      return "https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=400&h=300&fit=crop";
    }
  };

  // Handle card click
  const handleClick = () => {
    onProjectClick(project.project_id);
  };

  return (
    <div 
      className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-300 cursor-pointer" // Add cursor-pointer
      onClick={handleClick} // Add onClick handler
    >
      <div className="relative h-48">
        <img
          src={getProjectImage(project)}
          alt={project.project_name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3">
          <span className="bg-green-600 text-white px-2 py-1 rounded text-xs font-semibold">
            Completed
          </span>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-start justify-between mb-2">
          <h3 className="font-semibold text-gray-900 text-lg leading-tight">
            {project.project_name}
          </h3>
          <div className="bg-green-100 text-green-800 p-1 rounded ml-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>

        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
          {project.description}
        </p>

        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-center">
            <svg
              className="w-4 h-4 mr-2 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span>{project.location}</span>
          </div>

          {project.length_size && (
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                />
              </svg>
              <span>{project.length_size}</span>
            </div>
          )}

          {project.estimated_cost && (
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"
                />
              </svg>
              <span>{formatCost(project.estimated_cost)}</span>
            </div>
          )}

          {project.end_date && (
            <div className="flex items-center">
              <svg
                className="w-4 h-4 mr-2 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>Completed {formatDate(project.end_date)}</span>
            </div>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-gray-100">
          <div className="flex justify-between items-center text-sm">
            <span className="text-gray-500">Progress</span>
            <div className="flex items-center">
              <span className="text-green-600 font-semibold">100%</span>
              <div className="w-16 h-2 bg-gray-200 rounded-full ml-2">
                <div className="h-2 bg-green-500 rounded-full w-full"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletedProjects;